import { formatMoney } from '@theme/money-formatting';

const productRequests = new Map();
const SELECTOR = '[data-product-price-sync]';

function normalize(value) {
  return String(value || '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/[–—]/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9"+]+/g, ' ')
    .trim();
}

function getProductUrl(handle) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}products/${encodeURIComponent(handle)}.js`;
}

function fetchProduct(handle) {
  if (!handle) return Promise.resolve(null);
  if (!productRequests.has(handle)) {
    productRequests.set(
      handle,
      fetch(getProductUrl(handle), { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
    );
  }

  return productRequests.get(handle);
}

function getDefaultFinish(product) {
  const key = normalize(`${product?.handle || ''} ${product?.title || ''}`);
  if (key.includes('aeroglow')) return 'Black';
  if (key.includes('miniglow')) return 'White';
  return '';
}

function getMoneyFormat(root) {
  return root.dataset.productPriceSyncMoneyFormat || '${{amount}}';
}

function getCurrency(root) {
  return (
    root.dataset.productPriceSyncCurrency ||
    window.Shopify?.currency?.active ||
    window.Shopify?.currency?.shopCurrency ||
    'USD'
  );
}

function getFinishIndex(product) {
  return (product?.options || []).findIndex((option) => {
    const name = typeof option === 'string' ? option : option?.name;
    return /finish|colou?r/i.test(String(name || ''));
  });
}

function getVariantOptions(variant) {
  if (Array.isArray(variant?.options) && variant.options.length) return variant.options;
  return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
}

function getVariantFinish(product, variant, preferredFinish = '') {
  const finishIndex = getFinishIndex(product);
  const options = getVariantOptions(variant);
  if (finishIndex >= 0 && options[finishIndex]) return options[finishIndex];

  const preferredKey = normalize(preferredFinish);
  return (
    options.find((value) => {
      const key = normalize(value);
      return key.includes('black') || key.includes('white') || (preferredKey && key.includes(preferredKey));
    }) || ''
  );
}

function decorateVariant(product, variant, root, preferredFinish = '') {
  const priceAmount = Number(variant.price);
  const compareAtPriceAmount = Number(variant.compare_at_price || 0);
  const hasSale = compareAtPriceAmount > priceAmount && compareAtPriceAmount > 0;
  const salePercent = hasSale ? Math.round(((compareAtPriceAmount - priceAmount) * 100) / compareAtPriceAmount) : 0;
  const moneyFormat = getMoneyFormat(root);
  const currency = getCurrency(root);
  const finish = getVariantFinish(product, variant, preferredFinish);
  const baseUrl = root.dataset.productPriceSyncProductUrl || product.url || `/products/${product.handle}`;
  const url = new URL(baseUrl, window.location.origin);

  url.searchParams.set('variant', String(variant.id));
  if (finish) url.searchParams.set('finish', finish);

  return {
    id: String(variant.id),
    available: variant.available !== false,
    options: getVariantOptions(variant),
    finish,
    price: formatMoney(priceAmount, moneyFormat, currency),
    priceAmount,
    compareAtPrice: hasSale ? formatMoney(compareAtPriceAmount, moneyFormat, currency) : null,
    compareAtPriceAmount: hasSale ? compareAtPriceAmount : null,
    salePercent,
    url: `${url.pathname}${url.search}`,
  };
}

function valueMatches(value, requested) {
  const key = normalize(value);
  const requestedKey = normalize(requested);
  return Boolean(key && requestedKey && (key === requestedKey || key.includes(requestedKey) || requestedKey.includes(key)));
}

function findVariantByFinish(product, variants, finish) {
  if (!finish) return null;
  const finishIndex = getFinishIndex(product);

  return (
    variants.find((variant) => {
      const options = getVariantOptions(variant);
      if (finishIndex >= 0) return valueMatches(options[finishIndex], finish);
      return options.some((option) => valueMatches(option, finish));
    }) || null
  );
}

function findVariantBySelectedOptions(root, variants) {
  const selectedValues = [];

  root.querySelectorAll('[data-cpsbrg-product-option]').forEach((group) => {
    const index = Number(group.getAttribute('data-option-index'));
    const active = group.querySelector('[data-cpsbrg-option][aria-pressed="true"]');
    if (!Number.isNaN(index) && active instanceof HTMLElement) {
      selectedValues[index] = active.dataset.optionValue || '';
    }
  });

  if (!selectedValues.some(Boolean)) return null;

  return (
    variants.find((variant) =>
      selectedValues.every((value, index) => !value || valueMatches(getVariantOptions(variant)[index], value))
    ) || null
  );
}

function getUrlVariant(product, variants) {
  const params = new URLSearchParams(window.location.search);
  const finish = params.get('finish') || params.get('color');
  const finishVariant = finish ? findVariantByFinish(product, variants, finish) : null;
  const variantId = params.get('variant');

  return finishVariant || variants.find((variant) => String(variant.id) === String(variantId)) || null;
}

function getPreferredVariant(product, variants, root, requestedFinish = '', forceFinish = false) {
  if (forceFinish && requestedFinish) {
    const forcedFinishVariant = findVariantByFinish(product, variants, requestedFinish);
    if (forcedFinishVariant) return forcedFinishVariant;
  }

  if (root.dataset.productPriceSyncUseOptions === 'true') {
    const selectedOptionVariant = findVariantBySelectedOptions(root, variants);
    if (selectedOptionVariant) return selectedOptionVariant;
  }

  if (root.dataset.productPriceSyncUseUrl === 'true') {
    const urlVariant = getUrlVariant(product, variants);
    if (urlVariant) return urlVariant;
  }

  const finish =
    requestedFinish ||
    root.dataset.productPriceSyncFinish ||
    root.dataset.cpsbrgFinish ||
    getDefaultFinish(product);
  const finishVariant = findVariantByFinish(product, variants, finish);
  if (finishVariant) return finishVariant;

  const variantId = root.dataset.productPriceSyncVariantId;
  return (
    variants.find((variant) => String(variant.id) === String(variantId)) ||
    variants.find((variant) => variant.available !== false) ||
    variants[0] ||
    null
  );
}

function renderPrice(variant) {
  const hasSale = Boolean(variant.compareAtPrice && variant.salePercent > 0);

  if (!hasSale) {
    return `
      <div ref="priceContainer">
        <div class="price__regular">
          <span class="price">${variant.price}</span>
        </div>
        <div class="price__sale price__hidden"></div>
      </div>
    `;
  }

  return `
    <div ref="priceContainer">
      <div class="price__regular price__hidden">
        <span class="price">${variant.price}</span>
      </div>
      <div class="price__sale">
        <span class="price-item__group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price-item--sale price">${variant.price}</span>
        </span>
        <span class="price-item__group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="price-item--regular compare-at-price">${variant.compareAtPrice}</span>
        </span>
        <span class="price-sale-badge">-${variant.salePercent}% Sale</span>
      </div>
    </div>
  `;
}

function updatePriceRoot(root, variant) {
  if (!(root instanceof HTMLElement) || root.dataset.priceLocked === 'true') return;

  if (root.dataset.productPriceSyncPrice === 'text') {
    root.textContent = variant.price;
    return;
  }

  if (root.getAttribute('ref') === 'priceContainer') {
    const template = document.createElement('template');
    template.innerHTML = renderPrice(variant).trim();
    const priceContainer = template.content.firstElementChild;
    if (priceContainer) root.replaceWith(priceContainer);
    return;
  }

  const isSticky = root.hasAttribute('data-product-hero-sticky-price');
  const finishClass = Array.from(root.querySelector('[data-product-hero-sticky-finish]')?.classList || []).join(' ');

  root.innerHTML = renderPrice(variant);

  if (isSticky && variant.finish) {
    const finish = document.createElement('span');
    finish.className = finishClass || 'custom-product-hero__sticky-finish';
    finish.setAttribute('data-product-hero-sticky-finish', '');
    finish.textContent = `· ${variant.finish}`;
    root.append(finish);
  }
}

function updatePriceRoots(root, variant) {
  const roots = root.matches('[data-product-price-sync-price]')
    ? [root]
    : Array.from(
        root.querySelectorAll(
          '[data-product-price-sync-price], [data-product-hero-price], [data-product-hero-sticky-price], product-price [ref="priceContainer"]'
        )
      );

  roots.forEach((priceRoot) => updatePriceRoot(priceRoot, variant));
}

function updateActions(root, variant) {
  root.querySelectorAll('input[name="id"], [data-cpsbrg-variant-id]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.value = variant.id;
      input.disabled = !variant.available;
    }
  });

  root.querySelectorAll('.custom-product-action-form button, [ref="addToCartButton"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const available = variant.available !== false;
    button.disabled = !available;
    button.setAttribute('aria-disabled', available ? 'false' : 'true');

    const label = button.querySelector('.add-to-cart-text__content span span');
    if (label) {
      label.textContent = available ? root.dataset.cpsbrgAddText || button.dataset.actionLabel || label.textContent : 'Sold out';
    } else {
      button.textContent = available ? button.dataset.actionLabel || button.textContent : 'Sold out';
    }
  });
}

function updateLinks(root, variant) {
  const links = root.matches('[data-product-price-sync-link]')
    ? [root, ...root.querySelectorAll('[data-product-price-sync-link]')]
    : root.querySelectorAll('[data-product-price-sync-link]');

  links.forEach((link) => {
    if (link instanceof HTMLAnchorElement) link.href = variant.url;
  });
}

function updateSwatches(root, variant) {
  const finish = normalize(variant.finish);
  if (!finish) return;

  root.querySelectorAll('[data-gallery-finish], [data-product-price-sync-finish-option]').forEach((swatch) => {
    if (!(swatch instanceof HTMLElement)) return;
    const value = swatch.dataset.galleryFinish || swatch.dataset.productPriceSyncFinishOption;
    const active = valueMatches(value, finish);
    const activeClass = Array.from(swatch.classList).find((className) => className.includes('__swatch--active'));

    if (activeClass) swatch.classList.toggle(activeClass, active);
    swatch.setAttribute('aria-current', active ? 'true' : 'false');
  });
}

function updateSortingData(root, variant) {
  const card = root.matches('[data-ccpg-card]') ? root : root.closest('[data-ccpg-card]');
  if (card instanceof HTMLElement) {
    card.dataset.price = (variant.priceAmount / 100).toFixed(2);
  }
}

async function syncRoot(root, requestedFinish = '') {
  if (!(root instanceof HTMLElement)) return;

  const handle = root.dataset.productPriceSyncHandle || root.dataset.cpsbrgProductHandle;
  if (!handle) return;
  const forceFinish = Boolean(root.dataset.productPriceSyncForceFinish === 'true');

  try {
    const product = await fetchProduct(handle);
    const rawVariant = getPreferredVariant(product, product?.variants || [], root, requestedFinish, forceFinish);
    if (!product || !rawVariant) return;

    const variant = decorateVariant(product, rawVariant, root, requestedFinish);
    root.dataset.productPriceSyncVariantId = variant.id;
    if (variant.finish) root.dataset.productPriceSyncFinish = variant.finish;

    updatePriceRoots(root, variant);
    updateActions(root, variant);
    updateLinks(root, variant);
    updateSwatches(root, variant);
    updateSortingData(root, variant);
    root.dispatchEvent(
      new CustomEvent('product-price-sync:updated', {
        bubbles: true,
        detail: { product, root, variant },
      })
    );
    return variant;
  } catch (error) {
    console.warn('Unable to sync product pricing.', error);
    return null;
  }
}

function initRoot(root) {
  if (!(root instanceof HTMLElement) || root.dataset.productPriceSyncReady === 'true') return;

  root.dataset.productPriceSyncReady = 'true';
  syncRoot(root);

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const finishControl = target.closest('[data-gallery-finish], [data-cpsbrg-option], [data-product-price-sync-finish-option]');
    if (!(finishControl instanceof HTMLElement) || !root.contains(finishControl)) return;

    const finish = finishControl.dataset.galleryFinish || finishControl.dataset.productPriceSyncFinishOption || '';
    root.dataset.productPriceSyncForceFinish = finish ? 'true' : 'false';

    window.setTimeout(() => {
      syncRoot(root, finish).then((variant) => {
        delete root.dataset.productPriceSyncForceFinish;
        if (!variant || root.dataset.productPriceSyncUseUrl !== 'true' || !finish) return;

        try {
          window.history.replaceState({}, '', variant.url);
        } catch (error) {
          // The price and forms are already synced; URL writes are best effort.
        }
      });
    }, 0);
  });
}

function init(root = document) {
  root.querySelectorAll?.(SELECTOR).forEach(initRoot);
}

window.FancyMayProductPriceSync = {
  sync(root, options = {}) {
    if (!(root instanceof HTMLElement)) return Promise.resolve(null);
    const finish = typeof options.finish === 'string' ? options.finish : '';
    if (finish) root.dataset.productPriceSyncForceFinish = 'true';

    return syncRoot(root, finish).finally(() => {
      if (finish) delete root.dataset.productPriceSyncForceFinish;
    });
  },
};

init();

document.addEventListener('DOMContentLoaded', () => init());
document.addEventListener('shopify:section:load', (event) => init(event.target));
